/**
 * TitleScreenRenderer — title screen with orbiting shapes, pulsing prompt,
 * and input-adaptive hint text.
 *
 * Extracted from MenuRenderer as a composition class.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Keys } from 'bonkjs';
import { WORLD, MENU } from '../../constants';
import { getStartPrompt } from '../input-hints';

function monoStyle(size: number, color: number, bold = false): TextStyle {
    return new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: size,
        fill: color,
        fontWeight: bold ? 'bold' : 'normal',
    });
}

export class TitleScreenRenderer {
    private titleLine1: Text;
    private titleLine2: Text;
    private tagline: Text;
    private startPrompt: Text;
    private creditLine: Text;
    private orbitShapes: Graphics[] = [];

    constructor(container: Container, tapVirtualKey: (code: string) => void) {
        // Full-screen tap target — "tap anywhere to start"
        const titleHitArea = new Graphics();
        titleHitArea.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        titleHitArea.fill({ color: 0x000000, alpha: 0.001 });
        titleHitArea.eventMode = 'static';
        titleHitArea.cursor = 'pointer';
        titleHitArea.on('pointerdown', () => tapVirtualKey(Keys.Space));
        container.addChild(titleHitArea);

        const cx = WORLD.CANVAS_WIDTH / 2;

        // Title: "GEOMETRY" — magenta
        this.titleLine1 = new Text({
            text: 'GEOMETRY',
            style: monoStyle(MENU.TITLE_FONT_SIZE, MENU.TITLE_LINE1_COLOR, true),
        });
        this.titleLine1.anchor.set(0.5);
        this.titleLine1.position.set(cx, 320);
        container.addChild(this.titleLine1);

        // Title: "BLAST" — cyan
        this.titleLine2 = new Text({
            text: 'BLAST',
            style: monoStyle(MENU.TITLE_FONT_SIZE, MENU.TITLE_LINE2_COLOR, true),
        });
        this.titleLine2.anchor.set(0.5);
        this.titleLine2.position.set(cx, 400);
        container.addChild(this.titleLine2);

        // Tagline
        this.tagline = new Text({
            text: 'Artillery Smash Party',
            style: monoStyle(MENU.TAGLINE_FONT_SIZE, MENU.TAGLINE_COLOR),
        });
        this.tagline.anchor.set(0.5);
        this.tagline.position.set(cx, 460);
        container.addChild(this.tagline);

        // Pulsing start prompt
        this.startPrompt = new Text({
            text: 'PRESS SPACE TO START',
            style: monoStyle(MENU.PROMPT_FONT_SIZE, 0xffffff, true),
        });
        this.startPrompt.anchor.set(0.5);
        this.startPrompt.position.set(cx, 620);
        this.startPrompt.eventMode = 'static';
        this.startPrompt.cursor = 'pointer';
        this.startPrompt.on('pointerdown', () => tapVirtualKey(Keys.Space));
        container.addChild(this.startPrompt);

        // Credit line (hidden)
        this.creditLine = new Text({ text: '', style: monoStyle(MENU.CREDIT_FONT_SIZE, MENU.CREDIT_COLOR) });
        this.creditLine.visible = false;
        container.addChild(this.creditLine);

        // Orbiting geometric shapes
        const orbitColors = [0x33ff99, 0xff8800, 0x00ffff, 0xffd700];
        const orbitShapeTypes: ('circle' | 'square' | 'triangle' | 'hexagon')[] = ['circle', 'square', 'triangle', 'hexagon'];
        for (let i = 0; i < MENU.ORBIT_COUNT; i++) {
            const gfx = new Graphics();
            this.drawShape(gfx, 0, 0, MENU.ORBIT_SHAPE_SIZE, orbitColors[i], orbitShapeTypes[i]);
            gfx.alpha = 0.5;
            container.addChild(gfx);
            this.orbitShapes.push(gfx);
        }
    }

    update(elapsed: number, dt: number): void {
        // Input-adaptive text
        this.startPrompt.text = getStartPrompt();

        // Pulsing start prompt
        const pulse = MENU.PROMPT_PULSE_MIN +
            (1 - MENU.PROMPT_PULSE_MIN) * (0.5 + 0.5 * Math.sin(elapsed * MENU.PROMPT_PULSE_SPEED));
        this.startPrompt.alpha = pulse;

        // Orbiting shapes
        const cx = WORLD.CANVAS_WIDTH / 2;
        const cy = 360;
        for (let i = 0; i < this.orbitShapes.length; i++) {
            const phase = (i / this.orbitShapes.length) * Math.PI * 2;
            const angle = elapsed * MENU.ORBIT_SPEED + phase;
            const sizePulse = MENU.ORBIT_SHAPE_SIZE + Math.sin(elapsed * 2 + phase) * MENU.ORBIT_PULSE_AMP;

            const x = cx + Math.cos(angle) * MENU.ORBIT_RADIUS_X;
            const y = cy + Math.sin(angle) * MENU.ORBIT_RADIUS_Y;
            this.orbitShapes[i].position.set(x, y);

            const depthAlpha = 0.3 + 0.4 * (0.5 + 0.5 * Math.sin(angle));
            this.orbitShapes[i].alpha = depthAlpha;

            this.orbitShapes[i].clear();
            const colors = [0x33ff99, 0xff8800, 0x00ffff, 0xffd700];
            const shapes: ('circle' | 'square' | 'triangle' | 'hexagon')[] = ['circle', 'square', 'triangle', 'hexagon'];
            this.drawShape(this.orbitShapes[i], 0, 0, sizePulse, colors[i], shapes[i]);
        }
    }

    destroy(): void {
        // Children are destroyed with container by parent
    }

    private drawShape(gfx: Graphics, cx: number, cy: number, radius: number, color: number, shape: 'circle' | 'square' | 'triangle' | 'hexagon'): void {
        switch (shape) {
            case 'circle':
                gfx.circle(cx, cy, radius);
                gfx.stroke({ color, width: 2 });
                break;
            case 'square': {
                const half = radius * 0.8;
                gfx.rect(cx - half, cy - half, half * 2, half * 2);
                gfx.stroke({ color, width: 2 });
                break;
            }
            case 'triangle': {
                const pts: number[] = [];
                for (let i = 0; i < 3; i++) {
                    const angle = (i / 3) * Math.PI * 2 - Math.PI / 2;
                    pts.push(cx + Math.cos(angle) * radius);
                    pts.push(cy + Math.sin(angle) * radius);
                }
                gfx.poly(pts);
                gfx.stroke({ color, width: 2 });
                break;
            }
            case 'hexagon': {
                const pts: number[] = [];
                for (let i = 0; i < 6; i++) {
                    const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
                    pts.push(cx + Math.cos(angle) * radius);
                    pts.push(cy + Math.sin(angle) * radius);
                }
                gfx.poly(pts);
                gfx.stroke({ color, width: 2 });
                break;
            }
        }
    }
}
